import React, { useState, useMemo } from 'react';
import type { Expense, TimeEntry, TrustTransaction } from '../../../../billing/types/billing.types';
import { EXPENSE_CATEGORY_LABELS, TRUST_TRANSACTION_TYPE_LABELS } from '../../../../billing/types/billing.types';

interface TransactionsFeedSectionProps {
  expenses: Expense[];
  timeEntries: TimeEntry[];
  trustTransactions: TrustTransaction[];
  loading: boolean;
}

interface TransactionItem {
  id: string;
  date: string;
  type: 'expense' | 'time_entry' | 'trust';
  amount: number;
  description: string;
  vendor?: string;
  category?: string;
  hours?: number;
  rate?: number;
  isBilled: boolean;
  trustDirection?: 'debit' | 'credit';
}

const formatCurrency = (amount: number) =>
  new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(amount);

const formatDate = (dateString: string) =>
  new Date(dateString).toLocaleDateString('en-US', { month: 'short', day: 'numeric', year: 'numeric' });

const TransactionsFeedSection: React.FC<TransactionsFeedSectionProps> = ({
  expenses, timeEntries, trustTransactions, loading,
}) => {
  const [selectedType, setSelectedType] = useState('all');

  // Merge all transaction types into a unified chronological list
  const transactions: TransactionItem[] = useMemo(() => {
    const expenseItems: TransactionItem[] = expenses.map((exp) => ({
      id: exp.expense_id,
      date: exp.expense_date,
      type: 'expense' as const,
      amount: Number(exp.total_amount),
      description: exp.description,
      vendor: exp.vendor || undefined,
      category: EXPENSE_CATEGORY_LABELS[exp.expense_category] || exp.expense_category,
      isBilled: exp.is_billed,
    }));

    const timeItems: TransactionItem[] = timeEntries.map((te) => ({
      id: te.time_entry_id,
      date: te.entry_date,
      type: 'time_entry' as const,
      amount: Number(te.amount),
      description: te.description,
      hours: Number(te.duration),
      rate: te.rate != null ? Number(te.rate) : undefined,
      category: te.activity_category || undefined,
      isBilled: te.is_billed,
    }));

    const trustItems: TransactionItem[] = trustTransactions.map((txn) => ({
      id: txn.trust_transaction_id,
      date: txn.transaction_date,
      type: 'trust' as const,
      amount: Number(txn.amount),
      description: txn.description,
      vendor: txn.source_or_payee || undefined,
      category: TRUST_TRANSACTION_TYPE_LABELS[txn.transaction_type] || txn.transaction_type,
      isBilled: false,
      trustDirection: txn.direction,
    }));

    return [...expenseItems, ...timeItems, ...trustItems].sort(
      (a, b) => new Date(b.date).getTime() - new Date(a.date).getTime()
    );
  }, [expenses, timeEntries, trustTransactions]);

  const filteredTransactions = transactions.filter(
    (t) => selectedType === 'all' || t.type === selectedType
  );

  return (
    <div className="space-y-4">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <h4 className="text-lg font-medium text-gray-900 dark:text-white">
          Recent Transactions
          {!loading && (
            <span className="text-sm font-normal text-gray-400 ml-2">
              ({filteredTransactions.length})
            </span>
          )}
        </h4>
        <select
          value={selectedType}
          onChange={(e) => setSelectedType(e.target.value)}
          className="px-3 py-2 text-sm border border-gray-300 dark:border-gray-600 rounded-lg bg-white dark:bg-gray-700 text-gray-900 dark:text-white w-full sm:w-auto"
        >
          <option value="all">All Types</option>
          <option value="expense">Expenses</option>
          <option value="time_entry">Time Entries</option>
          <option value="trust">Trust Transactions</option>
        </select>
      </div>

      {loading ? (
        <div className="flex items-center justify-center py-8">
          <div className="animate-spin h-6 w-6 border-2 border-blue-500 border-t-transparent rounded-full" />
          <span className="ml-2 text-sm text-gray-500">Loading transactions...</span>
        </div>
      ) : (
        <div className="space-y-3">
          {filteredTransactions.map((transaction, i) => (
            <div key={transaction.id} className="lex-list-row border border-gray-200 dark:border-gray-700 rounded-lg p-4 hover:bg-gray-50 dark:hover:bg-gray-800 transition-colors" style={{ '--row-index': i } as React.CSSProperties}>
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-y-2 gap-x-4">
                <div className="flex items-start gap-3 flex-1 min-w-0">
                  {/* Type indicator */}
                  <div className={`flex-shrink-0 w-8 h-8 rounded-full flex items-center justify-center text-xs font-bold ${
                    transaction.type === 'time_entry'
                      ? 'bg-blue-100 text-blue-600 dark:bg-blue-900/30 dark:text-blue-400'
                      : transaction.type === 'trust'
                      ? 'bg-teal-100 text-teal-600 dark:bg-teal-900/30 dark:text-teal-400'
                      : 'bg-red-100 text-red-600 dark:bg-red-900/30 dark:text-red-400'
                  }`}>
                    {transaction.type === 'time_entry' ? 'T' : transaction.type === 'trust' ? '$' : 'E'}
                  </div>
                  <div className="min-w-0">
                    <div className="font-medium text-gray-900 dark:text-white truncate">
                      {transaction.description}
                    </div>
                    <div className="text-sm text-gray-500 dark:text-gray-400">
                      {formatDate(transaction.date)}
                      {transaction.vendor && ` \u2022 ${transaction.vendor}`}
                      {transaction.category && ` \u2022 ${transaction.category}`}
                      {transaction.hours != null && ` \u2022 ${transaction.hours}h`}
                      {transaction.rate != null && ` @ ${formatCurrency(transaction.rate)}/hr`}
                    </div>
                    <div className="flex items-center gap-2 mt-0.5">
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        transaction.type === 'time_entry'
                          ? 'bg-blue-50 text-blue-600 dark:bg-blue-900/20 dark:text-blue-400'
                          : transaction.type === 'trust'
                          ? 'bg-teal-50 text-teal-600 dark:bg-teal-900/20 dark:text-teal-400'
                          : 'bg-red-50 text-red-600 dark:bg-red-900/20 dark:text-red-400'
                      }`}>
                        {transaction.type === 'time_entry' ? 'Time' : transaction.type === 'trust' ? 'Trust' : 'Expense'}
                      </span>
                      <span className={`text-xs px-1.5 py-0.5 rounded ${
                        transaction.isBilled
                          ? 'bg-green-50 text-green-600 dark:bg-green-900/20 dark:text-green-400'
                          : 'bg-amber-50 text-amber-600 dark:bg-amber-900/20 dark:text-amber-400'
                      }`}>
                        {transaction.isBilled ? 'Billed' : 'Unbilled'}
                      </span>
                    </div>
                  </div>
                </div>
                <div className="text-right flex-shrink-0">
                  <div className={`text-lg font-bold tabular-nums ${
                    transaction.type === 'time_entry'
                      ? 'text-blue-600 dark:text-blue-400'
                      : transaction.type === 'trust'
                      ? transaction.trustDirection === 'credit'
                        ? 'text-green-600 dark:text-green-400'
                        : 'text-red-600 dark:text-red-400'
                      : 'text-red-600 dark:text-red-400'
                  }`}>
                    {transaction.type === 'trust'
                      ? `${transaction.trustDirection === 'credit' ? '+' : '-'}${formatCurrency(transaction.amount)}`
                      : `${transaction.type === 'expense' ? '-' : ''}${formatCurrency(transaction.amount)}`}
                  </div>
                </div>
              </div>
            </div>
          ))}

          {filteredTransactions.length === 0 && (
            <div className="text-center py-8 text-gray-500 dark:text-gray-400">
              No transactions found{selectedType !== 'all' ? ' matching your filter' : ' for this engagement'}.
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default TransactionsFeedSection;
